import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { FormGroup } from "@angular/forms";
import { NotificationType } from "libraries/interfaces/INotification";
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import { IQuotationCost } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { ICurrencyOption } from "libraries/service/resources/currency-list/currency-list-resource.service";
import { ISimpleSelectFieldConfig } from 'libraries/module/formbuilder/fields/simple-select/simple-select.interface';
import { SimpleSelectComponent } from 'libraries/module/formbuilder/fields/simple-select/simple-select.component';
import { CostFormGeneratorService } from 'app_quotation/pages/cost/services/cost-form-generator/cost-form-generator.service';
import { EMoveMode } from "libraries/enums/e-move-mode";
import { MultiselectComponent } from "libraries/module/formbuilder/fields/multiselect/multiselect.component";

@Component({
    selector: 'cost-grid-block-cost-currency',
    template: `
        <ng-template #template>
            <td mat-cell
                class="cost-cell cell cost-cell-currency"
                (click)="simpleSelectFieldFocus()"
                [class.editable]="total && isEditable"
                [class.in-edition-cell]="focuserFocused || simpleSelectFieldFocused">
                <div class="label">
                    {{wording.currency}}
                </div>
                <div class="no-edition" *ngIf="!total">
                    -
                </div>
                <input type="text"
                       #focuser
                       (focus)="onFocuserFocus()"
                       (keyup.enter)="simpleSelectFieldFocus()"
                       class="focuser"
                       *ngIf="total && isEditable"
                       (blur)="onFocuserBlur()">
                <multiselect #simpleSelectField
                             appendTo="body"
                             [fieldConfig]="fieldConfig"
                             [options]="fieldConfig.options"
                             [mustEmitChangeOnClick]="!!maxSelection"
                             [mustEmitChangeOnHide]="!maxSelection"
                             [isFull]="true"
                             [showHeader]="false"
                             [maxSelection]="maxSelection">
                </multiselect>

            </td>
        </ng-template>
    `,
    styles: [`
        :host {
            display: none
        }

        td.cost-cell.cost-cell-currency {
            text-align: right;
            position: sticky;
            z-index: 1;
            width: 50px;
            left: 615px;
            padding: 0;
            border-right: 1px solid rgba(189, 195, 199, 0.3);
            border-bottom: 1px solid rgba(189, 195, 199, 0.3);
        }

        .label {
            position: absolute;
            font-size: .75rem;
            top: .05rem;
            left: .1rem;
            opacity: 0.5;
        }

        .focuser {
            opacity: 0;
            width: 0;
            height: 0;
            position: absolute;
            z-index: -10;
        }

        .no-edition {
            padding: 0 .75rem;
        }

        .editable {
            cursor: pointer;
        }
        multiselect ::ng-deep p-multiSelect .p-multiselect {
            background: transparent !important;
            box-shadow: none !important;
            border : 0 !important;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostGridBlockCostCurrencyComponent implements OnInit, OnChanges {

    @Input()
    public total: IQuotationCost;
    @Input()
    public isEditable: boolean = true;
    @Input()
    public options: ICurrencyOption[] = [{value: "EUR", label: "€"}];
    @Input()
    public isEmpty: boolean;

    public readonly notificationType: typeof NotificationType = NotificationType;
    public readonly moveMode: typeof EMoveMode = EMoveMode;
    public readonly wording: ILabels = {
        currency: Yii.t('wui-quotation-js', 'Devise'),
    };

    public simpleSelectFieldFocused: boolean = false;
    public focuserFocused: boolean = false;
    public formGroup: FormGroup;
    public fieldConfig: ISimpleSelectFieldConfig;
    public maxSelection: number = 1;


    /**
     * Indicate that Edition is opened from keyboard navigation  in parent component
     */
    @Input()
    public isKeyboardOpen: boolean;


    @Output()
    public readonly onCurrencyChange: EventEmitter<IQuotationCost> = new EventEmitter<IQuotationCost>();
    @Output()
    public readonly onFocusField: EventEmitter<null> = new EventEmitter<null>();
    @Output()
    public readonly onMoveModeChange: EventEmitter<EMoveMode> = new EventEmitter<EMoveMode>();
    @ViewChild('template', {read: TemplateRef, static: true})
    private readonly template: TemplateRef<{}>;
    @ViewChild('simpleSelectField') private readonly simpleSelectField: MultiselectComponent;
    @ViewChild('focuser') private readonly focuser: ElementRef;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly costFormGeneratorService: CostFormGeneratorService,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
        this.initField();
        this.fieldConfig.formGroup.valueChanges
            .subscribe((cost: IQuotationCost) => {
                this.total = {...cost, currency : Array.isArray(cost.currency) ? cost.currency[0] : cost.currency};
                this.onCurrencyChange.emit(this.total);
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {

        if (changes.isKeyboardOpen && changes.isKeyboardOpen.currentValue && this.focuser) {
            this.focuser.nativeElement.focus();
        }

        if (this.fieldConfig) {
            if (changes && changes.options && changes.options.currentValue) {
                this.fieldConfig.options = changes.options.currentValue;
            }
            if (changes && changes.total && changes.total.currentValue) {
                this.fieldConfig.formGroup.get('currency').patchValue(
                    Array.isArray(changes.total.currentValue.currency) ? changes.total.currentValue.currency[0] : [changes.total.currentValue.currency],
                    {onlySelf: true, emitEvent: false}
                );
            }

            if (changes && changes.isEditable && changes.isEditable.currentValue !== changes.isEditable.previousValue) {
                this.fieldConfig.disabled = !this.isEditable;
            }
        }

    }

    public onFocuserFocus(): void {
        this.focuserFocused = true;
        this.simpleSelectFieldFocused = false;
        this.onFocusField.emit();
        this.onMoveModeChange.emit(EMoveMode.grid);
    }

    public onFocuserBlur(): void {
        setTimeout(()=>{
            this.focuserFocused = false;
        });
    }

    public simpleSelectFieldFocus(): void {
        if(this.isEditable){
            // this.simpleSelectField.multiSelect.focus();
        }
    }

    public onSimpleSelectFocus():void {
        if(this.isEditable){
            this.focuserFocused = false;
            this.simpleSelectFieldFocused = true;
            this.onMoveModeChange.emit(EMoveMode.componentCell);
        }

    }

    public blur(): void {
        this.focuserFocused = false;
        this.simpleSelectFieldFocused = false;
    }

    public removeBorders():void{
        if(this.isEditable){
            setTimeout(()=>{
                this.focuserFocused = false;
                this.simpleSelectFieldFocused = false;
            });
        }
    }

    private initField(): void {
        this.fieldConfig = {
            formGroup: this.costFormGeneratorService.generateCostGroup(this.total),
            formControlName: 'currency',
            options: this.options,
            disabled: !this.isEditable
        };
    }
}
